import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, RefreshCw, Pencil, X, Check, FlaskConical, Loader2, UserCheck, ImagePlus, Images, ChevronDown, ChevronRight, ChevronUp, CalendarDays, MessageSquare, Reply, CornerDownRight, Send, Copy } from "lucide-react";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, parseISO } from "date-fns";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Pagination } from "@/components/Pagination";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const priorityConfig = { P0: { badge: "bg-red-50 text-red-700 border-red-200" }, P1: { badge: "bg-amber-50 text-amber-700 border-amber-200" }, P2: { badge: "bg-blue-50 text-blue-700 border-blue-200" }, P3: { badge: "bg-slate-100 text-slate-600 border-slate-200" } };
const statusConfig = { "Not Started": { badge: "bg-slate-100 text-slate-600 border-slate-200", dot: "bg-slate-400" }, "In Progress": { badge: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" }, Passed: { badge: "bg-green-50 text-green-700 border-green-200", dot: "bg-green-500" }, Failed: { badge: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500" } };
const statusCycle = { "Not Started": "In Progress", "In Progress": "Passed", Passed: "Failed", Failed: "Not Started" };

const formatTimestamp = (ts) => {
  if (!ts) return "";
  const date = new Date(ts);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

// ─── UAT Comments Sub-Component ──────────────────────────────────────────────
const UATComments = ({ uatId, stakeholders }) => {
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [author, setAuthor] = useState("");
  const [commentText, setCommentText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchComments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/uat/${uatId}/comments`);
      setComments(res.data || []);
    } catch (e) { console.error(e); }
  }, [uatId]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  const submitComment = async () => {
    if (!author || !commentText.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/uat/${uatId}/comments`, { author_name: author, comment: commentText.trim() });
      setCommentText("");
      setReplyOpen(false);
      await fetchComments();
      setShowComments(true);
    } catch (e) { console.error(e); }
    setSubmitting(false);
  };

  const deleteComment = async (cid) => {
    try {
      await axios.delete(`${API}/uat/comments/${cid}`);
      await fetchComments();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="mb-3 py-2 px-3 rounded-lg bg-slate-50 border border-slate-100 ml-6">
      <div className="flex items-center justify-between">
        <button
          onClick={() => setShowComments(!showComments)}
          className="flex items-center gap-2 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
          data-testid={`toggle-comments-${uatId}`}
        >
          <MessageSquare className="w-3.5 h-3.5" />
          <span>Comments {comments.length > 0 ? `(${comments.length})` : ""}</span>
          {showComments ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </button>
        <button
          onClick={() => { setReplyOpen(!replyOpen); setShowComments(true); }}
          className="flex items-center gap-1.5 text-xs font-medium text-purple-600 hover:text-purple-700 transition-colors"
          data-testid={`add-comment-btn-${uatId}`}
        >
          <Reply className="w-3 h-3" /> Add Comment
        </button>
      </div>

      {/* Comments List */}
      {showComments && comments.length > 0 && (
        <div className="mt-3 ml-2 border-l-2 border-slate-200 pl-3 space-y-3" data-testid={`comments-list-${uatId}`}>
          {comments.map((c) => (
            <div key={c.id} className="flex items-start gap-2" data-testid={`comment-${c.id}`}>
              <CornerDownRight className="w-3 h-3 text-slate-300 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-700">{c.author_name}</span>
                    <span className="text-[10px] text-slate-400">{formatTimestamp(c.created_at)}</span>
                  </div>
                  <button onClick={() => deleteComment(c.id)} className="p-1 rounded text-slate-300 hover:text-red-500 hover:bg-red-50" data-testid={`delete-comment-${c.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
                <p className="text-xs text-slate-600 mt-0.5">{c.comment}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      {showComments && comments.length === 0 && !replyOpen && (
        <p className="mt-2 text-xs text-slate-400">No comments yet. Click "Add Comment" to start.</p>
      )}

      {/* Reply Form */}
      {replyOpen && (
        <div className="mt-3 ml-2 border-l-2 border-purple-200 pl-3 space-y-2" data-testid={`comment-form-${uatId}`}>
          <Select value={author} onValueChange={setAuthor}>
            <SelectTrigger className="h-8 text-xs" data-testid={`comment-author-${uatId}`}><SelectValue placeholder="Select your name" /></SelectTrigger>
            <SelectContent>
              {stakeholders.map((s) => (
                <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea placeholder="Write your comment..." rows={2} value={commentText} onChange={(e) => setCommentText(e.target.value)} className="text-xs" data-testid={`comment-text-${uatId}`} />
          <div className="flex gap-2">
            <Button onClick={submitComment} disabled={submitting || !author || !commentText.trim()} size="sm" className="h-7 text-xs bg-purple-600 hover:bg-purple-700 text-white" data-testid={`comment-submit-${uatId}`}>
              <Send className="w-3 h-3 mr-1" />{submitting ? "Sending..." : "Comment"}
            </Button>
            <Button onClick={() => setReplyOpen(false)} variant="outline" size="sm" className="h-7 text-xs">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export const UATPanel = () => {
  const { selectedProduct } = useAuth();
  const productId = selectedProduct?.id;
  const [items, setItems] = useState([]);
  const [uatPage, setUatPage] = useState(1);
  const UAT_PAGE_SIZE = 10;
  const [stakeholders, setStakeholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [formData, setFormData] = useState({ title: "", what_testing: "", how_to_test: "", expected_result: "", priority: "P1", assigned_to: [], team: "", due_date: "" });
  const [submitting, setSubmitting] = useState(false);
  const [filterStatus, setFilterStatus] = useState(null);
  const [testerEditId, setTesterEditId] = useState(null);
  const [testerName, setTesterName] = useState("");
  const [uploadingImage, setUploadingImage] = useState({});
  const [showImages, setShowImages] = useState({});
  const [expandedIds, setExpandedIds] = useState({});
  const [teams, setTeams] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const imageInputRefs = useRef({});

  const toggleExpand = (id) => setExpandedIds((p) => ({ ...p, [id]: !p[id] }));

  const fetchItems = useCallback(async () => {
    try {
      const params = productId ? { product_id: productId } : {};
      const [uatRes, sRes, teamsRes] = await Promise.all([
        axios.get(`${API}/uat`, { params }),
        axios.get(`${API}/stakeholders`, { params }),
        axios.get(`${API}/teams`),
      ]);
      setItems(uatRes.data);
      setStakeholders(sRes.data);
      setTeams(teamsRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [productId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const addItem = async () => {
    if (!formData.title.trim()) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/uat`, { ...formData, product_id: productId });
      setItems((p) => [...p, r.data]);
      setFormData({ title: "", what_testing: "", how_to_test: "", expected_result: "", priority: "P1", assigned_to: [], team: "", due_date: "" });
      setShowForm(false);
    } catch (e) { console.error(e); }
    setSubmitting(false);
  };

  const cycleStatus = async (item) => {
    const next = statusCycle[item.status];
    try { await axios.patch(`${API}/uat/${item.id}`, { status: next }); setItems((p) => p.map((r) => (r.id === item.id ? { ...r, status: next } : r))); } catch (e) { console.error(e); }
  };

  const saveEdit = async (item) => {
    try { const r = await axios.patch(`${API}/uat/${item.id}`, editData); setItems((p) => p.map((x) => (x.id === item.id ? r.data : x))); setEditingId(null); } catch (e) { console.error(e); }
  };

  const deleteItem = async (id) => {
    try { await axios.delete(`${API}/uat/${id}`); setItems((p) => p.filter((x) => x.id !== id)); } catch (e) { console.error(e); }
  };

  const duplicateItem = async (id) => {
    try {
      const r = await axios.post(`${API}/uat/${id}/duplicate`);
      setItems((p) => [...p, r.data]);
    } catch (e) { console.error(e); }
  };

  const saveTester = async (item) => {
    if (!testerName) return;
    try {
      const r = await axios.patch(`${API}/uat/${item.id}`, { tester_name: testerName });
      setItems((p) => p.map((x) => (x.id === item.id ? r.data : x)));
      setTesterEditId(null);
      setTesterName("");
    } catch (e) { console.error(e); }
  };

  const uploadImage = async (itemId, file) => {
    if (!file) return;
    setUploadingImage((p) => ({ ...p, [itemId]: true }));
    try {
      const sigRes = await axios.get(`${API}/cloudinary/signature?folder=uat&resource_type=auto`);
      const sig = sigRes.data;
      const fd = new FormData();
      fd.append("file", file);
      fd.append("api_key", sig.api_key);
      fd.append("timestamp", sig.timestamp);
      fd.append("signature", sig.signature);
      fd.append("folder", sig.folder);
      fd.append("resource_type", "auto");
      const cloudRes = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/auto/upload`, { method: "POST", body: fd });
      const cloudData = await cloudRes.json();
      if (cloudData.secure_url) {
        const item = items.find((i) => i.id === itemId);
        const newImages = [...(item.images || []), { url: cloudData.secure_url, public_id: cloudData.public_id, uploaded_at: new Date().toISOString(), format: cloudData.format, resource_type: cloudData.resource_type }];
        await axios.patch(`${API}/uat/${itemId}`, { images: newImages });
        setItems((p) => p.map((x) => x.id === itemId ? { ...x, images: newImages } : x));
        setShowImages((p) => ({ ...p, [itemId]: true }));
      } else {
        alert("Upload failed: " + (cloudData.error?.message || "Unknown error"));
      }
    } catch (err) {
      alert("Image upload failed: " + err.message);
    }
    setUploadingImage((p) => ({ ...p, [itemId]: false }));
    if (imageInputRefs.current[itemId]) imageInputRefs.current[itemId].value = "";
  };

  const deleteImage = async (itemId, imageIndex) => {
    const item = items.find((i) => i.id === itemId);
    const newImages = (item.images || []).filter((_, i) => i !== imageIndex);
    try {
      await axios.patch(`${API}/uat/${itemId}`, { images: newImages });
      setItems((p) => p.map((x) => x.id === itemId ? { ...x, images: newImages } : x));
    } catch (err) { console.error(err); }
  };

  const toggleImages = (itemId) => setShowImages((p) => ({ ...p, [itemId]: !p[itemId] }));

  const moveItem = async (itemId, direction) => {
    try {
      await axios.post(`${API}/uat/reorder`, { id: itemId, direction });
      fetchItems();
    } catch (err) { console.error(err); }
  };

  const filtered = items.filter((r) => !filterStatus || r.status === filterStatus);
  const counts = { total: items.length, "Not Started": items.filter((r) => r.status === "Not Started").length, "In Progress": items.filter((r) => r.status === "In Progress").length, Passed: items.filter((r) => r.status === "Passed").length, Failed: items.filter((r) => r.status === "Failed").length };

  return (
    <>
    <div data-testid="uat-panel">
      <p className="text-xs font-semibold tracking-widest uppercase text-[#A78BFA] mb-3">Testing</p>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 dark:text-slate-100 font-['Outfit']">UAT</h1>
      <p className="text-base text-slate-500 dark:text-slate-400 mt-2">User acceptance testing items and results</p>
      <Separator className="my-8 bg-slate-200 dark:bg-slate-700" />

      <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="uat-counters">
        <Badge className="bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-100">{counts.total} Total</Badge>
        {["Not Started", "In Progress", "Passed", "Failed"].map((s) => { const c = statusConfig[s]; return <Badge key={s} className={`${c.badge} border hover:opacity-90`}><span className={`w-2 h-2 rounded-full ${c.dot} mr-1.5 inline-block`} />{counts[s]} {s}</Badge>; })}
      </div>

      <div className="flex items-center gap-3 mb-6">
        <span className="text-xs font-medium text-slate-500">Filter:</span>
        <select value={filterStatus || ""} onChange={(e) => setFilterStatus(e.target.value || null)} className="h-8 px-3 rounded-lg text-xs font-semibold border border-slate-200 bg-white text-slate-700 cursor-pointer" data-testid="uat-filter-status">
          <option value="">All</option><option value="Not Started">Not Started</option><option value="In Progress">In Progress</option><option value="Passed">Passed</option><option value="Failed">Failed</option>
        </select>
      </div>

      {loading ? <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : (
        <>
        <div className="flex flex-col gap-3" data-testid="uat-list">
          {filtered.slice((uatPage - 1) * UAT_PAGE_SIZE, uatPage * UAT_PAGE_SIZE).map((item) => {
            const sCfg = statusConfig[item.status] || statusConfig["Not Started"];
            const pCfg = priorityConfig[item.priority] || priorityConfig.P1;
            const isEditing = editingId === item.id;
            return (
              <Card key={item.id} className={`border border-slate-200 shadow-sm bg-white hover:shadow-md transition-all ${item.status === "Passed" ? "opacity-70" : ""}`} data-testid={`uat-card-${item.id}`}>
                <CardContent className="p-0">
                  {isEditing ? (
                    <div className="p-5 space-y-3">
                      <Input value={editData.title ?? item.title} onChange={(e) => setEditData((p) => ({ ...p, title: e.target.value }))} className="font-semibold" placeholder="Test case title" data-testid={`uat-edit-title-${item.id}`} />
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="text-[10px] font-semibold tracking-widest uppercase text-purple-600 mb-1 block">What You're Testing</label>
                          <Textarea value={editData.what_testing ?? item.what_testing ?? ""} onChange={(e) => setEditData((p) => ({ ...p, what_testing: e.target.value }))} rows={3} placeholder="The element or feature being tested..." data-testid={`uat-edit-what-${item.id}`} />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold tracking-widest uppercase text-purple-600 mb-1 block">How to Test It</label>
                          <Textarea value={editData.how_to_test ?? item.how_to_test ?? ""} onChange={(e) => setEditData((p) => ({ ...p, how_to_test: e.target.value }))} rows={3} placeholder="Step-by-step instructions..." data-testid={`uat-edit-how-${item.id}`} />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold tracking-widest uppercase text-purple-600 mb-1 block">What Should Happen</label>
                          <Textarea value={editData.expected_result ?? item.expected_result ?? ""} onChange={(e) => setEditData((p) => ({ ...p, expected_result: e.target.value }))} rows={3} placeholder="Expected outcome..." data-testid={`uat-edit-expected-${item.id}`} />
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Select value={editData.priority ?? item.priority} onValueChange={(val) => setEditData((p) => ({ ...p, priority: val }))}><SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="P0">P0</SelectItem><SelectItem value="P1">P1</SelectItem><SelectItem value="P2">P2</SelectItem><SelectItem value="P3">P3</SelectItem></SelectContent></Select>
                        <select value="" onChange={(e) => {
                            const val = e.target.value;
                            if (!val) return;
                            const current = editData.assigned_to ?? (Array.isArray(item.assigned_to) ? item.assigned_to : item.assigned_to ? [item.assigned_to] : []);
                            if (!current.includes(val)) {
                              setEditData((p) => ({ ...p, assigned_to: [...current, val] }));
                            }
                          }} className="h-8 px-2 rounded-md text-xs font-medium border border-slate-200 bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-purple-500/30 cursor-pointer w-48" data-testid={`uat-edit-assigned-${item.id}`}>
                          <option value="">Add assignee...</option>
                          {stakeholders.filter(s => {
                            const current = editData.assigned_to ?? (Array.isArray(item.assigned_to) ? item.assigned_to : item.assigned_to ? [item.assigned_to] : []);
                            return !current.includes(s.name);
                          }).map((s) => (<option key={s.id} value={s.name}>{s.name}</option>))}
                        </select>
                        <Select value={editData.team ?? item.team ?? ""} onValueChange={(val) => setEditData((p) => ({ ...p, team: val === "_none" ? "" : val }))}>
                          <SelectTrigger className="w-44 h-8 text-xs" data-testid={`uat-edit-team-${item.id}`}><SelectValue placeholder="Select team..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">No Team</SelectItem>
                            {teams.map((t) => (<SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>))}
                          </SelectContent>
                        </Select>
                        <Popover>
                          <PopoverTrigger asChild>
                            <button className="h-8 px-3 rounded-md text-xs font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 flex items-center gap-2" data-testid={`uat-edit-duedate-${item.id}`}>
                              <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                              {(editData.due_date ?? item.due_date) ? (() => { try { return format(parseISO(editData.due_date ?? item.due_date), "MMM d, yyyy"); } catch { return editData.due_date ?? item.due_date; } })() : "Set due date"}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar mode="single" selected={(() => { try { return (editData.due_date ?? item.due_date) ? parseISO(editData.due_date ?? item.due_date) : undefined; } catch { return undefined; } })()} onSelect={(date) => setEditData((p) => ({ ...p, due_date: date ? format(date, "yyyy-MM-dd") : "" }))} />
                          </PopoverContent>
                        </Popover>
                        <div className="flex gap-1 ml-auto">
                          <Button size="sm" onClick={() => saveEdit(item)} className="h-7 bg-green-600 hover:bg-green-700 text-white text-xs"><Check className="w-3.5 h-3.5 mr-1" />Save</Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="h-7 text-xs"><X className="w-3.5 h-3.5 mr-1" />Cancel</Button>
                        </div>
                      </div>
                      {/* Assigned chips in edit */}
                      {(editData.assigned_to ?? (Array.isArray(item.assigned_to) ? item.assigned_to : item.assigned_to ? [item.assigned_to] : [])).length > 0 && (
                        <div className="flex items-center gap-1 flex-wrap">
                          {(editData.assigned_to ?? (Array.isArray(item.assigned_to) ? item.assigned_to : item.assigned_to ? [item.assigned_to] : [])).map((name, i) => (
                            <span key={i} className="inline-flex items-center gap-1 bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full text-[11px] font-medium border border-purple-200">
                              {name}
                              <button onClick={() => {
                                const current = editData.assigned_to ?? (Array.isArray(item.assigned_to) ? item.assigned_to : item.assigned_to ? [item.assigned_to] : []);
                                setEditData((p) => ({ ...p, assigned_to: current.filter(n => n !== name) }));
                              }} className="hover:text-red-500 transition-colors"><X className="w-3 h-3" /></button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Clickable header */}
                      <div
                        className="px-5 pt-4 pb-2 flex items-start justify-between gap-4 cursor-pointer select-none"
                        onClick={() => toggleExpand(item.id)}
                        data-testid={`uat-toggle-${item.id}`}
                      >
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <div className="flex flex-col -mr-1" onClick={(e) => e.stopPropagation()}>
                            <button onClick={() => moveItem(item.id, "up")} className="p-0.5 rounded hover:bg-slate-100 text-slate-300 hover:text-slate-600 transition-colors" title="Move Up" data-testid={`uat-move-up-${item.id}`}>
                              <ChevronUp className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => moveItem(item.id, "down")} className="p-0.5 rounded hover:bg-slate-100 text-slate-300 hover:text-slate-600 transition-colors" title="Move Down" data-testid={`uat-move-down-${item.id}`}>
                              <ChevronDown className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <h3 className={`text-base font-semibold font-['Outfit'] ${item.status === "Passed" ? "text-slate-400 line-through" : "text-slate-800"}`}>
                            <span className="text-purple-600 font-mono mr-2">{String(item.uat_number || 0).padStart(3, '0')}.</span>
                            {item.title}
                          </h3>
                          <Badge className={`${pCfg.badge} border text-[11px] font-semibold hover:opacity-90`}>{item.priority}</Badge>
                          {item.assigned_to && (Array.isArray(item.assigned_to) ? item.assigned_to.length > 0 : !!item.assigned_to) && (
                            <span className="text-[11px] text-slate-400 font-medium">
                              &rarr; {Array.isArray(item.assigned_to) ? item.assigned_to.join(", ") : item.assigned_to}
                            </span>
                          )}
                          {item.team && (
                            <span className="text-[10px] font-semibold text-teal-700 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/30 px-2 py-0.5 rounded-md border border-teal-200 dark:border-teal-800">
                              {item.team}
                            </span>
                          )}
                          {item.due_date && (
                            <span className="text-[11px] text-slate-400 font-medium flex items-center gap-1">
                              <CalendarDays className="w-3 h-3" />
                              {(() => { try { return format(parseISO(item.due_date), "MMM d"); } catch { return item.due_date; } })()}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => cycleStatus(item)} className={`status-badge px-3 py-1 rounded-full text-xs font-semibold ${sCfg.badge} border flex items-center gap-1.5 hover:opacity-80`} data-testid={`uat-status-${item.id}`}><RefreshCw className="w-3 h-3" />{item.status}</button>
                        </div>
                      </div>

                      {/* Expandable body */}
                      {expandedIds[item.id] && (
                        <div className="px-5 pb-4">
                          {/* 3-Column Test Details */}
                          {(item.what_testing || item.how_to_test || item.expected_result || item.description) && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 ml-6">
                              <div className="bg-slate-50 rounded-lg border border-slate-100 p-4">
                                <p className="text-[10px] font-semibold tracking-widest uppercase text-purple-600 mb-2">What You're Testing</p>
                                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{item.what_testing || item.description || ""}</p>
                              </div>
                              <div className="bg-slate-50 rounded-lg border border-slate-100 p-4">
                                <p className="text-[10px] font-semibold tracking-widest uppercase text-purple-600 mb-2">How to Test It</p>
                                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{item.how_to_test || ""}</p>
                              </div>
                              <div className="bg-slate-50 rounded-lg border border-slate-100 p-4">
                                <p className="text-[10px] font-semibold tracking-widest uppercase text-purple-600 mb-2">What Should Happen</p>
                                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{item.expected_result || ""}</p>
                              </div>
                            </div>
                          )}

                          {/* Tester section */}
                          <div className="flex items-center gap-3 mb-3 py-2 px-3 rounded-lg bg-slate-50 border border-slate-100 ml-6">
                            <UserCheck className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                            {testerEditId === item.id ? (
                              <div className="flex items-center gap-2 flex-1">
                                <Select value={testerName} onValueChange={setTesterName}>
                                  <SelectTrigger className="h-7 text-xs w-48" data-testid={`uat-tester-select-${item.id}`}><SelectValue placeholder="Select tester" /></SelectTrigger>
                                  <SelectContent>{stakeholders.map((s) => (<SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>))}</SelectContent>
                                </Select>
                                <Button size="sm" onClick={() => saveTester(item)} disabled={!testerName} className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white" data-testid={`uat-tester-save-${item.id}`}><Check className="w-3 h-3" /></Button>
                                <Button size="sm" variant="outline" onClick={() => { setTesterEditId(null); setTesterName(""); }} className="h-7 text-xs"><X className="w-3 h-3" /></Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 flex-1">
                                {item.tester_name ? (
                                  <>
                                    <span className="text-xs font-medium text-slate-700">Tested by: <span className="font-semibold">{item.tester_name}</span></span>
                                    {item.tested_at && <span className="text-[10px] text-slate-400">{formatTimestamp(item.tested_at)}</span>}
                                  </>
                                ) : (
                                  <span className="text-xs text-slate-400">No tester assigned</span>
                                )}
                                <button onClick={() => { setTesterEditId(item.id); setTesterName(item.tester_name || ""); }} className="ml-auto p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600" data-testid={`uat-tester-edit-${item.id}`}>
                                  <Pencil className="w-3 h-3" />
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Attachments */}
                          <div className="mb-3 py-2 px-3 rounded-lg bg-slate-50 border border-slate-100 ml-6">
                            <div className="flex items-center justify-between">
                              <button onClick={() => toggleImages(item.id)} className="flex items-center gap-2 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors">
                                <Images className="w-3.5 h-3.5" />
                                <span>Attachments {(item.images || []).length > 0 ? `(${item.images.length})` : ""}</span>
                                {showImages[item.id] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                              </button>
                              <label className="cursor-pointer">
                                <input type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.tif,.webp,.svg,.heic,.heif" className="hidden" ref={(el) => (imageInputRefs.current[item.id] = el)} onChange={(e) => uploadImage(item.id, e.target.files?.[0])} />
                                <span className="flex items-center gap-1.5 text-xs font-medium text-purple-600 hover:text-purple-700">
                                  {uploadingImage[item.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImagePlus className="w-3 h-3" />}
                                  {uploadingImage[item.id] ? "Uploading..." : "Add"}
                                </span>
                              </label>
                            </div>
                            {showImages[item.id] && (item.images || []).length > 0 && (
                              <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {(item.images || []).map((img, idx) => (
                                  <div key={idx} className="relative group">
                                    {img.format === 'pdf' || img.resource_type === 'raw' ? (
                                      <div className="w-full h-20 flex flex-col items-center justify-center bg-white rounded border border-slate-200"><span className="text-xl">📄</span><span className="text-[9px] text-slate-500">PDF</span></div>
                                    ) : (
                                      <img src={img.url} alt={`Attachment ${idx + 1}`} className="w-full h-20 object-cover rounded border border-slate-200" />
                                    )}
                                    <button onClick={() => deleteImage(item.id, idx)} className="absolute top-0.5 right-0.5 p-0.5 rounded bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity" title="Delete"><Trash2 className="w-2.5 h-2.5" /></button>
                                    <a href={img.url} target="_blank" rel="noopener noreferrer" className="absolute bottom-0.5 left-0.5 text-[9px] bg-black/50 text-white px-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">View</a>
                                  </div>
                                ))}
                              </div>
                            )}
                            {showImages[item.id] && (item.images || []).length === 0 && (<p className="mt-2 text-xs text-slate-400">No attachments yet</p>)}
                          </div>

                          {/* Comments */}
                          <UATComments uatId={item.id} stakeholders={stakeholders} />

                          {/* Edit / Duplicate / Delete */}
                          <div className="flex items-center gap-2 pl-6">
                            <button onClick={() => { setEditingId(item.id); setEditData({}); setExpandedIds((p) => ({ ...p, [item.id]: true })); }} className="p-1.5 rounded hover:bg-slate-100 text-slate-300 hover:text-slate-600" data-testid={`uat-edit-${item.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                            <button onClick={() => duplicateItem(item.id)} className="p-1.5 rounded hover:bg-blue-50 text-slate-300 hover:text-blue-600" title="Duplicate" data-testid={`uat-duplicate-${item.id}`}><Copy className="w-3.5 h-3.5" /></button>
                            <button onClick={() => setDeleteTarget({ id: item.id, label: item.title })} className="p-1.5 rounded hover:bg-red-50 text-slate-300 hover:text-red-500" data-testid={`uat-delete-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
          {filtered.length === 0 && <div className="text-center py-12 text-slate-400"><FlaskConical className="w-10 h-10 mx-auto mb-3 opacity-40" /><p className="text-sm">No UAT items yet.</p></div>}
        </div>
        <Pagination currentPage={uatPage} totalItems={filtered.length} pageSize={UAT_PAGE_SIZE} onPageChange={setUatPage} />
        </>
      )}

      {/* Add UAT Form */}
      {showForm ? (
        <Card className="mt-6 border border-slate-200 shadow-sm bg-white" data-testid="add-uat-form">
          <CardContent className="p-6">
            <h3 className="text-sm font-semibold text-slate-700 font-['Outfit'] mb-4">Add UAT Item</h3>
            <div className="space-y-3">
              <Input placeholder="Test case title" value={formData.title} onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))} data-testid="uat-title-input" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] font-semibold tracking-widest uppercase text-purple-600 mb-1 block">What You're Testing</label>
                  <Textarea placeholder="The element or feature being tested..." value={formData.what_testing} onChange={(e) => setFormData((p) => ({ ...p, what_testing: e.target.value }))} rows={3} data-testid="uat-what-input" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold tracking-widest uppercase text-purple-600 mb-1 block">How to Test It</label>
                  <Textarea placeholder="Step-by-step instructions..." value={formData.how_to_test} onChange={(e) => setFormData((p) => ({ ...p, how_to_test: e.target.value }))} rows={3} data-testid="uat-how-input" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold tracking-widest uppercase text-purple-600 mb-1 block">What Should Happen</label>
                  <Textarea placeholder="Expected outcome..." value={formData.expected_result} onChange={(e) => setFormData((p) => ({ ...p, expected_result: e.target.value }))} rows={3} data-testid="uat-expected-input" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Select value={formData.priority} onValueChange={(val) => setFormData((p) => ({ ...p, priority: val }))}><SelectTrigger className="w-40 h-9" data-testid="uat-priority-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="P0">P0 - Critical</SelectItem><SelectItem value="P1">P1 - High</SelectItem><SelectItem value="P2">P2 - Medium</SelectItem><SelectItem value="P3">P3 - Low</SelectItem></SelectContent></Select>
                <select value="" onChange={(e) => {
                    const val = e.target.value;
                    if (!val) return;
                    if (!formData.assigned_to.includes(val)) {
                      setFormData((p) => ({ ...p, assigned_to: [...p.assigned_to, val] }));
                    }
                  }} className="h-9 px-3 rounded-md text-sm border border-slate-200 bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-purple-500/30 cursor-pointer w-48" data-testid="uat-assigned-input">
                  <option value="">Add assignee...</option>
                  {stakeholders.filter(s => !formData.assigned_to.includes(s.name)).map((s) => (<option key={s.id} value={s.name}>{s.name}</option>))}
                </select>
                <Select value={formData.team} onValueChange={(val) => setFormData((p) => ({ ...p, team: val === "_none" ? "" : val }))}>
                  <SelectTrigger className="w-44 h-9" data-testid="uat-team-select"><SelectValue placeholder="Select team..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">No Team</SelectItem>
                    {teams.map((t) => (<SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>))}
                  </SelectContent>
                </Select>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="h-9 px-3 rounded-md text-sm border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 flex items-center gap-2" data-testid="uat-duedate-input">
                      <CalendarDays className="w-4 h-4 text-slate-400" />
                      {formData.due_date ? (() => { try { return format(parseISO(formData.due_date), "MMM d, yyyy"); } catch { return formData.due_date; } })() : "Set due date"}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={(() => { try { return formData.due_date ? parseISO(formData.due_date) : undefined; } catch { return undefined; } })()} onSelect={(date) => setFormData((p) => ({ ...p, due_date: date ? format(date, "yyyy-MM-dd") : "" }))} />
                  </PopoverContent>
                </Popover>
              </div>
              {formData.assigned_to.length > 0 && (
                <div className="flex items-center gap-1 flex-wrap">
                  {formData.assigned_to.map((name, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full text-xs font-medium border border-purple-200">
                      {name}
                      <button onClick={() => setFormData((p) => ({ ...p, assigned_to: p.assigned_to.filter(n => n !== name) }))} className="hover:text-red-500 transition-colors"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={addItem} disabled={submitting || !formData.title.trim()} className="bg-[#0F172A] hover:bg-slate-800 text-white" data-testid="uat-submit-btn"><Plus className="w-4 h-4 mr-2" />{submitting ? "Adding..." : "Add UAT Item"}</Button>
                <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Button onClick={() => setShowForm(true)} className="mt-6 bg-[#0F172A] hover:bg-slate-800 text-white shadow-sm" data-testid="add-uat-btn"><Plus className="w-4 h-4 mr-2" />Add UAT Item</Button>
      )}
    </div>
    <ConfirmDeleteDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={() => deleteItem(deleteTarget?.id)} title="Delete UAT Item" description={`Are you sure you want to delete "${deleteTarget?.label}"? This action cannot be undone.`} />
    </>
  );
};
